// src/pages/public/Privacy.js
import React from "react";
import { Helmet } from 'react-helmet-async';
import { motion } from "framer-motion";
import { COMPANY } from "../../utils/constants";

const LAST_UPDATED = "June 5, 2025";

export default function Privacy() {
  return (
    <div style={{ paddingTop: "var(--header-h)" }}>
      <Helmet>
        <title>Privacy - Aimble I.T. Solutions</title>
        <meta name="description" content="Read the Privacy Policy for Aimble I.T. Solutions. Learn how we collect, use, and protect your personal data when you use our website and IT services." />
        <link rel="canonical" href="https://www.aimbletech.com/privacy" />
      </Helmet>
      <section style={{ padding: "64px 0 96px" }}>
        <div className="container" style={{ maxWidth: 760 }}>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>
            <div className="section-label">Legal</div>
            <h1 className="heading-1" style={{ marginBottom: 8 }}>Privacy Policy</h1>
            <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", marginBottom: 48 }}>
              Last updated: {LAST_UPDATED}
            </p>

            <div className="prose">
              <Section title="1. Who We Are">
                <p>
                  Aimble I.T. Solutions ("AimbleTech", "we", "us", or "our") is a registered
                  IT services company located at {COMPANY.address}. We operate the website
                  at <a href="https://www.aimbletech.com">www.aimbletech.com</a> and
                  provide web development, payment solutions, and virtual assistant services.
                </p>
                <p>
                  We are registered with the Bureau of Internal Revenue (BIR) and the
                  Department of Trade and Industry (DTI) of the Philippines.
                </p>
              </Section>

              <Section title="2. Information We Collect">
                <p>We collect information you provide directly to us, including:</p>
                <ul>
                  <li><strong>Account information:</strong> Name, email address, and profile photo when you register using Google, Facebook, or email.</li>
                  <li><strong>Contact form submissions:</strong> Name, email, phone number, and message content.</li>
                  <li><strong>Support tickets:</strong> Issue descriptions and communication history.</li>
                  <li><strong>Payment information:</strong> We do not store credit card data. Payments are processed by Maya (PayMaya Philippines, Inc.), a BSP-regulated payment processor. We only store transaction references and amounts.</li>
                  <li><strong>Usage data:</strong> Pages visited, browser type, IP address, and device type via standard web analytics.</li>
                </ul>
              </Section>

              <Section title="3. How We Use Your Information">
                <p>We use the information we collect to:</p>
                <ul>
                  <li>Create and manage your client portal account</li>
                  <li>Process and fulfill service orders and subscriptions</li>
                  <li>Send transactional emails (order confirmations, invoices, renewal reminders)</li>
                  <li>Respond to your support tickets and inquiries</li>
                  <li>Improve our website and services</li>
                  <li>Comply with legal obligations under Philippine law</li>
                </ul>
                <p>
                  We do not sell, rent, or share your personal information with third parties
                  for marketing purposes.
                </p>
              </Section>

              <Section title="4. Facebook Login">
                <p>
                  When you choose to sign in using Facebook Login, we receive your public
                  profile information (name, email address, and profile picture) from Facebook
                  as permitted by your Facebook privacy settings.
                </p>
                <p>
                  We use this information solely to create and manage your account on our
                  platform. We do not post to your Facebook profile, access your friends list,
                  or request any permissions beyond basic profile and email.
                </p>
                <p>
                  You can revoke our access to your Facebook account at any time by visiting
                  your Facebook Settings → Apps and Websites → Remove AimbleTech.
                </p>
              </Section>

              <Section title="5. Google Login">
                <p>
                  When you sign in with Google, we receive your name, email address, and
                  profile photo from Google. We use this information only to authenticate
                  you and manage your account. We do not access your Gmail, Google Drive,
                  or any other Google services.
                </p>
              </Section>

              <Section title="6. Data Storage and Security">
                <p>
                  Your data is stored securely on Google Firebase (Firestore), which is
                  hosted on Google Cloud infrastructure with industry-standard encryption
                  at rest and in transit (TLS/SSL). Firebase is compliant with ISO 27001,
                  SOC 2, and other major security certifications.
                </p>
                <p>
                  We implement Firestore security rules to ensure that each user can only
                  access their own data. Administrative access is restricted to authorized
                  Aimble I.T. Solutions personnel only.
                </p>
              </Section>

              <Section title="7. Data Retention">
                <p>
                  We retain your personal data for as long as your account is active or
                  as needed to provide you services. We also retain records as required
                  by Philippine tax and business regulations (generally 10 years for
                  financial records under BIR requirements).
                </p>
                <p>
                  If you request deletion of your account, we will delete or anonymize
                  your personal data within 30 days, except where we are required to
                  retain it by law.
                </p>
              </Section>

              <Section title="8. Your Rights">
                <p>You have the right to:</p>
                <ul>
                  <li><strong>Access</strong> the personal data we hold about you</li>
                  <li><strong>Correct</strong> inaccurate or incomplete data</li>
                  <li><strong>Delete</strong> your account and associated personal data</li>
                  <li><strong>Export</strong> a copy of your data</li>
                  <li><strong>Withdraw consent</strong> for data processing at any time</li>
                </ul>
                <p>
                  To exercise these rights, contact us at{" "}
                  <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> or
                  visit our{" "}
                  <a href="/data-deletion">Data Deletion page</a>.
                </p>
              </Section>

              <Section title="9. Cookies">
                <p>
                  We use essential cookies to maintain your login session and remember
                  your preferences. We do not use advertising cookies or cross-site
                  tracking cookies. You can disable cookies in your browser settings,
                  though this may prevent you from logging into your account.
                </p>
              </Section>

              <Section title="10. Third-Party Services">
                <p>We use the following third-party services that may process your data:</p>
                <ul>
                  <li><strong>Google Firebase</strong> — Authentication, database, and hosting</li>
                  <li><strong>Maya (PayMaya Philippines)</strong> — Payment processing</li>
                  <li><strong>Facebook Login</strong> — Optional social authentication</li>
                  <li><strong>Google Calendar</strong> — Appointment booking</li>
                  <li><strong>SendGrid</strong> — Transactional email delivery</li>
                </ul>
                <p>
                  Each of these services has their own privacy policy and data practices.
                </p>
              </Section>

              <Section title="11. Children's Privacy">
                <p>
                  Our services are not directed to children under the age of 13. We do
                  not knowingly collect personal information from children. If you believe
                  we have inadvertently collected information from a child, please contact
                  us immediately.
                </p>
              </Section>

              <Section title="12. Changes to This Policy">
                <p>
                  We may update this Privacy Policy from time to time. We will notify
                  registered users by email of material changes. The "Last updated" date
                  at the top of this page will always reflect the most recent revision.
                </p>
              </Section>

              <Section title="13. Contact Us">
                <p>
                  If you have any questions about this Privacy Policy or how we handle
                  your data, please contact us:
                </p>
                <ul>
                  <li><strong>Email:</strong> <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></li>
                  <li><strong>Facebook:</strong> <a href={COMPANY.facebook} target="_blank" rel="noopener noreferrer">@aimbleph</a></li>
                  <li><strong>Address:</strong> {COMPANY.address}</li>
                </ul>
              </Section>
            </div>
          </motion.div>
        </div>
      </section>

      <style>{`
        .prose h2 { font-size: 1.125rem; font-weight: 600; margin: 2.5rem 0 1rem; color: var(--text); font-family: var(--font-head); }
        .prose p { color: var(--text-muted); line-height: 1.8; margin-bottom: 1rem; }
        .prose ul { padding-left: 1.5rem; margin-bottom: 1rem; }
        .prose li { color: var(--text-muted); line-height: 1.8; margin-bottom: 0.4rem; }
        .prose strong { color: var(--text); font-weight: 600; }
        .prose a { color: var(--accent); }
        .section-divider { border: none; border-top: 1px solid var(--border); margin: 2rem 0; }
      `}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: "2.5rem" }}>
      <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "1rem",
        color: "var(--text)", fontFamily: "var(--font-head)" }}>{title}</h2>
      {children}
      <hr className="section-divider" />
    </div>
  );
}
